import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Copy, Loader2 } from 'lucide-react';
import { STATUS, addDays, fmtDay, ymd, errMsg } from './porterUi';

const monday = (s) => { const d = new Date(s + 'T00:00:00'); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return ymd(d); };

export const WeeklyPlanner = ({ authFetch, onOpenDay }) => {
  const [week, setWeek] = useState(() => monday(ymd(new Date())));
  const [data, setData] = useState(null);
  const [driverF, setDriverF] = useState('');
  const [porterF, setPorterF] = useState('');

  const load = useCallback(() => {
    authFetch('get', `/admin/crew-board?start=${week}&days=7`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [authFetch, week]);
  useEffect(() => { load(); }, [load]);

  const copyLastWeek = async () => {
    if (!window.confirm(`Copy last week's crews onto the week of ${fmtDay(week)}?`)) return;
    let copied = 0;
    const skipped = [];
    for (let i = 0; i < 7; i++) {
      try {
        const { data: r } = await authFetch('post', '/admin/crew/copy', { from_date: addDays(week, i - 7), to_date: addDays(week, i) });
        copied += r.copied;
        skipped.push(...r.skipped);
      } catch (e) { toast.error(errMsg(e)); }
    }
    toast.success(`Copied ${copied} assignment${copied === 1 ? '' : 's'}.`);
    if (skipped.length) toast.warning(`Skipped ${skipped.length}: ${skipped.slice(0, 3).join('; ')}`, { duration: 8000 });
    load();
  };

  const rows = useMemo(() => {
    if (!data) return [];
    const ds = [...data.drivers, { id: null, name: 'Unassigned driver' }].filter(d => !driverF || d.id === driverF);
    return ds.map(d => ({ d, jobs: data.jobs.filter(j => (j.driver_id || null) === d.id && (!porterF || j.crew.some(c => c.porter.id === porterF))) }))
      .filter(r => r.jobs.length || (driverF && r.d.id === driverF));
  }, [data, driverF, porterF]);

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" />;
  const dayStats = data.dates.map(d => ({
    d,
    free: Object.values(data.porter_status[d] || {}).filter(s => s === 'available').length,
    short: data.jobs.filter(j => j.booking.date === d).reduce((n, j) => n + j.porters_short, 0),
  }));

  return (
    <div data-testid="weekly-planner">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setWeek(addDays(week, -7))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Previous week" data-testid="planner-prev"><ChevronLeft className="w-4 h-4" /></button>
        <div className="h-10 px-3 rounded-md bg-amber-100 border border-amber-200 text-sm font-black text-[color:var(--mv-navy)] inline-flex items-center" data-testid="planner-week-label">{fmtDay(week)} – {fmtDay(addDays(week, 6))}</div>
        <button onClick={() => setWeek(addDays(week, 7))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Next week" data-testid="planner-next"><ChevronRight className="w-4 h-4" /></button>
        <select value={driverF} onChange={(e) => setDriverF(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="planner-filter-driver">
          <option value="">All drivers</option>
          {data.drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={porterF} onChange={(e) => setPorterF(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="planner-filter-porter">
          <option value="">All porters</option>
          {data.porters.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <button onClick={copyLastWeek} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-xs font-bold inline-flex items-center gap-1 ml-auto" data-testid="planner-copy-week"><Copy className="w-3.5 h-3.5" />Copy last week</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-[900px] w-full text-xs">
          <thead>
            <tr className="bg-slate-50">
              <th className="text-left p-2 w-36">Driver</th>
              {dayStats.map(s => (
                <th key={s.d} className="text-left p-2 align-top">
                  <button type="button" onClick={() => onOpenDay(s.d)} className="font-black text-[color:var(--mv-navy)] hover:text-[color:var(--mv-orange)]" data-testid={`planner-day-${s.d}`}>{fmtDay(s.d)}</button>
                  <div className="font-normal text-slate-500">{s.free} free{s.short ? <span className="text-red-700 font-bold" data-testid={`planner-short-${s.d}`}> · {s.short} short</span> : ''}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No jobs this week for this filter.</td></tr>}
            {rows.map(({ d, jobs }) => (
              <tr key={d.id || 'none'} className="border-t border-slate-100 align-top" data-testid={`planner-row-${d.id || 'none'}`}>
                <td className="p-2 font-bold text-[color:var(--mv-navy)]"><span className="inline-block w-2.5 h-2.5 rounded-full mr-1" style={{ background: d.color || '#94a3b8' }} />{d.name}</td>
                {data.dates.map(day => (
                  <td key={day} className="p-1.5">
                    {jobs.filter(j => j.booking.date === day).map(j => (
                      <button key={j.id} type="button" onClick={() => onOpenDay(day)} className={`block w-full text-left mb-1 rounded-md border px-1.5 py-1 ${j.staffing === 'full' ? 'border-emerald-200 bg-emerald-50' : j.staffing === 'under' ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`} data-testid={`planner-job-${j.ref}`}>
                        <div className="font-black">{j.booking.time} #{j.ref}{j.van ? ` · ${j.van.reg}` : ''}</div>
                        <div className="text-slate-600 truncate">{j.crew.length ? j.crew.map(c => c.porter.name).join(', ') : 'No porters'}</div>
                        {j.porters_short > 0 && <div className="text-red-700 font-bold">Needs {j.porters_short} more</div>}
                      </button>
                    ))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
        {['working', 'assigned', 'available', 'absent', 'day_off'].map(k => <span key={k} className="inline-flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-full ${STATUS[k].dot}`} />{STATUS[k].label}</span>)}
      </div>
    </div>
  );
};
